import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ContentBox,
  ContentPage,
  DescriptionList,
  InputTextarea,
  ModalDefault,
  ModalForm,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import PaymentScheduleTab from "../../../../components/PaymentScheduleTab";
import ProjectHoursTab from "../../../../components/ProjectHoursTab";
import VariableValuesTab from "../../../../components/VariableValuesTab";
import { formatPhone } from "../../../../helpers";
import { hasPermission } from "@helpers/permissions";
import { DocumentNumber, escapeHtml, usePageHeader } from "@components/ow";
import {
  ContractDetailSkeleton,
  DataTab,
  DetailLink,
  DetailTabs,
  PatternSection,
  RemunerationSection,
  ServicesSection,
  TermSection,
  detailTitle,
} from "../../Common/ContractDetail";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export default function ContractDetails() {
  const navigate = useNavigate();
  const contractService = useService("contracts");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [pauseError, setPauseError] = useState("");
  const params = useParams();
  const [searchParams] = useSearchParams();
  // "Ver contratos" só lê; editar, pausar e reativar seguem só ADM
  const isAdmin = JSON.parse(localStorage.getItem("user_ow") || "{}")?.role === "ADM";
  // Cronograma e Valores (lançamentos) com "Ver cronogramas", só leitura
  const canReadSchedules = hasPermission("payment_schedules:read");
  const requestedTab = searchParams.get("tab") || "dados";
  const defaultTab =
    isAdmin || requestedTab === "horas" || (canReadSchedules && ["cronograma", "valores"].includes(requestedTab))
      ? requestedTab
      : "dados";
  const [activeTab, setActiveTab] = useState(defaultTab);

  const contractor = data.contractor || {};
  const name = contractor.name || "Contrato";
  usePageHeader({
    title: loading ? "Contrato" : detailTitle(contractor.name, data),
    breadcrumb: [
      { page: "Contratos", route: "/c/contracts" },
      { page: loading ? "Contrato" : name, route: `/c/contracts/${params.id}` },
    ],
  });

  /** O projeto abre na Visão Empresa com "Ver projetos" ou algum item de horas nele. */
  function canOpenProject(projectId) {
    return (
      isAdmin ||
      hasPermission("projects:read") ||
      hasPermission("project_hours:read", projectId) ||
      hasPermission("project_hours:approve", projectId)
    );
  }

  useEffect(() => {
    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchContract() {
    try {
      const result = await contractService.get(params.id);
      if (result && result.data) {
        setData(result.data);
      } else {
        throw new Error("Erro ao carregar dados do contrato");
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao carregar contrato";
      toast.error(errorMessage);
      setTimeout(() => navigate("/c/contracts"), 2000);
    }
    setLoading(false);
  }

  function openPause() {
    setPauseReason("");
    setPauseError("");
    setShowPauseModal(true);
  }

  function closePause() {
    if (loadingAction) return;
    setShowPauseModal(false);
  }

  async function handlePauseConfirm() {
    if (!pauseReason || pauseReason.trim() === "") {
      setPauseError("O motivo da pausa é obrigatório");
      return;
    }

    setLoadingAction(true);
    try {
      const result = await contractService.pauseContract(params.id, pauseReason);
      if (result && !result.error) {
        toast.success("Contrato pausado com sucesso!");
        setShowPauseModal(false);
        setPauseReason("");
        fetchContract();
      } else {
        throw new Error(result?.msg || "Erro ao pausar contrato");
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao pausar contrato";
      toast.error(errorMessage);
    }
    setLoadingAction(false);
  }

  async function handleReactivate() {
    setLoadingAction(true);
    try {
      const result = await contractService.reactivateContract(params.id);
      if (result && !result.error) {
        toast.success("Contrato reativado com sucesso!");
        setShowReactivateModal(false);
        fetchContract();
      } else {
        throw new Error(result?.msg || "Erro ao reativar contrato");
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao reativar contrato";
      toast.error(errorMessage);
    }
    setLoadingAction(false);
  }

  if (loading) return <ContractDetailSkeleton />;

  const projectName = data.project ? (
    canOpenProject(data.project.id) ? <DetailLink to={`/c/projects/${data.project.id}`} text={data.project.name} /> : data.project.name
  ) : null;

  const pattern = data.contract_pattern ? (
    isAdmin ? (
      <DetailLink to={`/c/contract-patterns/${data.contract_pattern.id}/edit`} text={data.contract_pattern.name} />
    ) : (
      data.contract_pattern.name
    )
  ) : null;

  const tabs = [
    {
      value: "dados",
      label: "Dados",
      content: (
        <DataTab
          contract={data}
          left={
            <>
              <DescriptionList
                $title="Prestador"
                $items={[
                  { label: "Nome", value: contractor.name },
                  ...(contractor.company_name ? [{ label: "Razão social", value: contractor.company_name }] : []),
                  { label: "Documento", value: contractor.document ? <DocumentNumber value={contractor.document} showKind={false} /> : null },
                  { label: "E-mail", value: contractor.email },
                  { label: "Telefone", value: formatPhone(contractor.phone) },
                ]}
              />
              <RemunerationSection contract={data} />
            </>
          }
          right={
            <>
              <TermSection contract={data} project={projectName} />
              <PatternSection contract={data} title="Padrão de contrato" pattern={pattern} />
              <ServicesSection serviceCodes={data.service_codes} cnaes={contractor.cnaes} />
              {data.additional_rights && typeof data.additional_rights === "object" && data.additional_rights.vacation !== undefined ? (
                <DescriptionList
                  $title="Direitos adicionais"
                  $columns={1}
                  $items={[{ label: "Férias", value: data.additional_rights.vacation ? "Sim" : "Não" }]}
                />
              ) : null}
            </>
          }
        />
      ),
    },
  ];

  if (canReadSchedules) {
    tabs.push({
      value: "cronograma",
      label: "Pagamentos",
      content: (
        <PaymentScheduleTab
          contractId={parseInt(params.id)}
          contract={data}
          isAdmin={true}
          canManage={isAdmin}
          canDownloadInvoice={hasPermission("tax_invoices:read")}
        />
      ),
    });
  }

  // Fora do ADM, com "Ver horas" no projeto
  if (data.project_id && hasPermission("project_hours:read", data.project_id)) {
    tabs.push({
      value: "horas",
      label: "Horas",
      content: (
        <ProjectHoursTab
          projectId={data.project_id}
          userId={data.user_id}
          isAdmin={true}
          canDecide={hasPermission("project_hours:approve", data.project_id) && hasPermission("project_hours:reject", data.project_id)}
          hourMarkingModel={data.hour_marking_model}
          hourTrackingMode={data.hour_tracking_mode}
          contract={data}
        />
      ),
    });
  }

  if (canReadSchedules && data.remuneration_type === "VARIABLE") {
    tabs.push({
      value: "valores",
      label: "Valores variáveis",
      content: <VariableValuesTab contractId={parseInt(params.id)} contract={data} canManage={isAdmin} />,
    });
  }

  const actions =
    isAdmin && data.status === "ACTIVE" ? (
      <>
        <Button $text="Pausar contrato" $variant="outline" $noWidth $isDisabled={loadingAction} $action={openPause} />
        <Button $text="Editar contrato" $variant="outline" $noWidth $action={() => navigate(`/c/contracts/${params.id}/edit`)} />
      </>
    ) : isAdmin && data.status === "PAUSED" ? (
      <Button $text="Reativar contrato" $variant="outline" $noWidth $isDisabled={loadingAction} $action={() => setShowReactivateModal(true)} />
    ) : null;

  return (
    <ContentPage>
      <ContentBox>
        <DetailTabs tabs={tabs} value={activeTab} onChange={setActiveTab} actions={actions} />
      </ContentBox>

      {showPauseModal && (
        <ModalForm
          $showModal={showPauseModal}
          $setShowModal={closePause}
          $noClose={loadingAction}
          $isLoading={loadingAction}
          $size="medium"
          $title="Pausar contrato"
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closePause, variant: "outline", isDisabled: loadingAction },
            { text: "Pausar contrato", action: handlePauseConfirm, variant: "primary", isDisabled: loadingAction },
          ]}
        >
          <ModalBody>
            <Text
              $text="Você está prestes a pausar o contrato de <strong>%s</strong>. Durante a pausa, não serão gerados cronogramas de pagamento automáticos."
              $replaces={[escapeHtml(contractor.name || "prestador")]}
              $variant="paragraph_small_medium"
              $color="N700"
              $colorEmphasis="N900"
            />
            <InputTextarea
              $idForm="pause-reason"
              $label="Motivo da pausa"
              $variant="default"
              $isRequired
              $errors={pauseError}
              rows={3}
              value={pauseReason}
              onChange={(e) => {
                setPauseReason(e.target.value);
                if (pauseError) setPauseError("");
              }}
              placeholder="Ex.: pausa temporária do projeto, aguardando aprovação…"
            />
          </ModalBody>
        </ModalForm>
      )}

      {showReactivateModal && (
        <ModalDefault
          $showModal={showReactivateModal}
          $setShowModal={() => !loadingAction && setShowReactivateModal(false)}
          $noClose={loadingAction}
          $size="medium"
          $title="Reativar contrato?"
          $contentBackground="N0"
          $footer={
            <>
              <Button
                $text="Cancelar"
                $variant="outline"
                $noWidth
                $isDisabled={loadingAction}
                $action={() => setShowReactivateModal(false)}
              />
              <Button $text="Reativar contrato" $variant="primary" $noWidth $isLoading={loadingAction} $action={handleReactivate} />
            </>
          }
        >
          <Text
            $text="O contrato de <strong>%s</strong> volta a ficar ativo."
            $replaces={[escapeHtml(contractor.name || "prestador")]}
            $variant="paragraph_small_medium"
            $color="N700"
            $colorEmphasis="N900"
          />
        </ModalDefault>
      )}
    </ContentPage>
  );
}
